import React from "react";
import { AppDataContext } from "./AppDataContext";
import { getInitialLang } from "../i18n/i18n";
// repo'lar
import { settingsRepo } from "../api/modules/settings/repo";
import { whyUsRepo } from "../api/modules/whyUs/repo";
import { categoryRepo } from "../api/modules/category/repo";
import { sliderRepo } from "../api/modules/slider/repo";
import { blogRepo } from "../api/modules/blog/repo";
import { contactRepo } from "../api/modules/contact/repo";
import { pageConfigRepo } from "../api/modules/pageConfig/repo";
import { socialRepo } from "../api/modules/social/repo";
import { serviceRegionRepo } from "../api/modules/serviceRegion/repo";
import { companyInfoRepo } from "../api/modules/companyInfo/repo";
import { investorRelationsRepo } from "../api/modules/investorRelations/repo";
import { historyRepo } from "../api/modules/history/repo";
import { aboutRepo } from "../api/modules/about/repo";
import { certificateRepo } from "../api/modules/certificate/repo";
import { rndRepo } from "../api/modules/rnd/repo";
import { boardRepo } from "../api/modules/board/repo";
import { productRepo } from "../api/modules/product/repo";
import { sssRepo } from "../api/modules/sss/repo";
// basit in-memory cache (isteğe bağlı TTL ekleyebilirsin)
const cache = new Map();

export default class AppDataProvider extends React.Component {
  state = {
    // i18n
    lang: getInitialLang(),
    settings: null,
    pageConfig: {},
    whyUs: null,
    category: null,
    slider: null,
    blog: null,
    contact: null,
    social: null,
    serviceRegion: null,
    companyInfo: null,
    investorRelations: null,
    history: null,
    about: null,
    certificate: null,
    rnd: null,
    board: null,
    product: null,
    sss: null,
    loading: true,
    error: null,
  };

  componentDidMount() {
    document.documentElement.lang = this.state.lang;
    this.bootstrap();
  }
  componentDidUpdate(_, prevState) {
    // dil değiştiyse head vs. güncellemek istersen
    if (prevState.lang !== this.state.lang) {
      this.applyHead(this.state.settings);
    }
  }
  setLang = (lang) => {
    this.setState({ lang });
    localStorage.setItem("lang", lang);
    document.documentElement.lang = lang;
  };

  bootstrap = async () => {
    this.setState({ loading: true, error: null });
    try {
      // Uygulama için kritik olanları paralel çekelim
      // (örn. settings + menus)
      const [settings] = await Promise.all([
        this.loadSettings(),
        this.loadWhyUs(),
        this.loadCategory(),
        this.loadSlider(),
        this.loadBlog(),
        this.loadContact(),
        this.loadSocial(),
        this.loadServiceRegion(),
        this.loadCompanyInfo(),
        this.loadInvestorRelations(),
        this.loadHistory(),
        this.loadAbout(),
        this.loadCertificate(),
        this.loadRnd(),
        this.loadBoard(),
        this.loadProduct(), // tüm ürünler
        this.loadFeaturedProduct(), // öne çıkanlar
        this.loadSss(),
        this.loadPageConfig("contact"),
        this.loadPageConfig("blog"),
        this.loadPageConfig("footer"),
        this.loadPageConfig("serviceRegion"),
        this.loadPageConfig("team"),
        this.loadPageConfig("product"),
        this.loadPageConfig("faq"),
      ]);

      // İsteğe bağlı: favicon & title burada set edilebilir
      this.applyHead(settings);

      this.setState({ loading: false });
    } catch (error) {
      console.error(error);
      this.setState({ error, loading: false });
    }
  };

  applyHead = (settings) => {
    try {
      if (!settings) return;
      const lang = this.state.lang;
      const t =
        settings.translations?.[lang] ||
        settings.translations?.tr ||
        settings.translations?.en;

      if (t?.title) document.title = t.title;
      if (settings?.favicon) {
        const base = process.env.REACT_APP_SAFKAR_API_FILE_URL || "";
        const link =
          document.querySelector("link[rel*='icon']") ||
          document.createElement("link");
        link.rel = "icon";
        link.href = `${base.replace(/\/+$/, "")}${settings.favicon}`;
        document.head.appendChild(link);
      }
      const title =
        settings?.translations?.tr?.title || settings?.translations?.en?.title;
      if (title) document.title = title;
    } catch (e) {
      console.warn("head apply failed", e);
    }
  };

  // ---- Loaders (tek yerde) ----
  loadSettings = async () => {
    const ckey = "settings";
    if (cache.has(ckey)) {
      const settings = cache.get(ckey);
      this.setState({ settings });
      return settings;
    }
    const res = await settingsRepo.get(); // senin repo: {logo, favicon, translations...}
    cache.set(ckey, res);
    this.setState({ settings: res });
    return res;
  };

  loadWhyUs = async () => {
    const key = "whyUs";
    if (cache.has(key)) {
      const whyUs = cache.get(key);
      this.setState({ whyUs });
      return whyUs;
    }
    const data = await whyUsRepo.get();
    cache.set(key, data);
    this.setState({ whyUs: data });
    return data;
  };

  loadCategory = async () => {
    const key = "category";
    if (cache.has(key)) {
      const category = cache.get(key);
      this.setState({ category });
      return category;
    }
    const data = await categoryRepo.get();
    cache.set(key, data);
    this.setState({ category: data });
    return data;
  };

  loadSlider = async () => {
    const key = "slider";
    if (cache.has(key)) {
      const slider = cache.get(key);
      this.setState({ slider });
      return slider;
    }
    const data = await sliderRepo.get();
    cache.set(key, data);
    this.setState({ slider: data });
    return data;
  };

  loadBlog = async () => {
    const key = "blog";
    if (cache.has(key)) {
      const blog = cache.get(key);
      this.setState({ blog });
      return blog;
    }
    const data = await blogRepo.get();
    cache.set(key, data);
    this.setState({ blog: data });
    return data;
  };

  loadSocial = async () => {
    const key = "social";
    if (cache.has(key)) {
      const social = cache.get(key);
      this.setState({ social });
      return social;
    }
    const data = await socialRepo.get();
    cache.set(key, data);
    this.setState({ social: data });
    return data;
  };

  loadContact = async () => {
    const key = "contact";
    if (cache.has(key)) {
      const contact = cache.get(key);
      this.setState({ contact });
      return contact;
    }
    const data = await contactRepo.get();
    cache.set(key, data);
    this.setState({ contact: data });
    return data;
  };

  loadServiceRegion = async () => {
    const key = "serviceRegion";
    if (cache.has(key)) {
      const serviceRegion = cache.get(key);
      this.setState({ serviceRegion });
      return serviceRegion;
    }
    const data = await serviceRegionRepo.get();
    cache.set(key, data);
    this.setState({ serviceRegion: data });
    return data;
  };

  loadCompanyInfo = async () => {
    const key = "companyInfo";
    if (cache.has(key)) {
      const companyInfo = cache.get(key);
      this.setState({ companyInfo });
      return companyInfo;
    }
    const data = await companyInfoRepo.get();
    cache.set(key, data);
    this.setState({ companyInfo: data });
    return data;
  };

  loadInvestorRelations = async () => {
    const key = "investorRelations";
    if (cache.has(key)) {
      const investorRelations = cache.get(key);
      this.setState({ investorRelations });
      return investorRelations;
    }
    const data = await investorRelationsRepo.get();
    cache.set(key, data);
    this.setState({ investorRelations: data });
    return data;
  };

  loadHistory = async () => {
    const key = "history";
    if (cache.has(key)) {
      const historyData = cache.get(key);
      this.setState({ history: historyData });
      return historyData;
    }
    const data = await historyRepo.get();
    cache.set(key, data);
    this.setState({ history: data });
    return data;
  };

  loadAbout = async () => {
    const key = "about";
    if (cache.has(key)) {
      const aboutData = cache.get(key);
      this.setState({ about: aboutData });
      return aboutData;
    }
    const data = await aboutRepo.get();
    cache.set(key, data);
    this.setState({ about: data });
    return data;
  };

  loadCertificate = async () => {
    const key = "certificate";
    if (cache.has(key)) {
      const certificateData = cache.get(key);
      this.setState({ certificate: certificateData });
      return certificateData;
    }
    const data = await certificateRepo.get();
    cache.set(key, data);
    this.setState({ certificate: data });
    return data;
  };

  loadRnd = async () => {
    const key = "rnd";
    if (cache.has(key)) {
      const rndData = cache.get(key);
      this.setState({ rnd: rndData });
      return rndData;
    }
    const data = await rndRepo.get();
    cache.set(key, data);
    this.setState({ rnd: data });
    return data;
  };

  loadBoard = async () => {
    const key = "board";
    if (cache.has(key)) {
      const boardData = cache.get(key);
      this.setState({ board: boardData });
      return boardData;
    }
    const data = await boardRepo.get();
    cache.set(key, data);
    this.setState({ board: data });
    return data;
  };

  loadProduct = async () => {
    const key = "product:all";
    if (cache.has(key)) {
      const data = cache.get(key);
      this.setState({ product: data });
      return data;
    }
    const data = await productRepo.get({
      featuredOnly: false,
      status: true,
      limit: 1000,
    });
    cache.set(key, data);
    this.setState({ product: data });
    return data;
  };

  loadFeaturedProduct = async () => {
    const key = "product:featured";
    if (cache.has(key)) {
      const data = cache.get(key);
      this.setState({ featuredProduct: data });
      return data;
    }
    const data = await productRepo.get({
      featuredOnly: true,
      status: true,
      limit: 1000,
    });
    cache.set(key, data);
    this.setState({ featuredProduct: data });
    return data;
  };

  loadSss = async () => {
    const key = "sss";
    if (cache.has(key)) {
      const sss = cache.get(key);
      this.setState({ sss });
      return sss;
    }
    const data = await sssRepo.get();
    cache.set(key, data);
    this.setState({ sss: data });
    return data;
  };

  loadPageConfig = async (pageKey) => {
    const cacheKey = `pageConfig:${pageKey}`;
    if (cache.has(cacheKey)) {
      const config = cache.get(cacheKey);
      this.setState((prev) => ({
        pageConfig: { ...prev.pageConfig, [pageKey]: config },
      }));
      return config;
    }

    const config = await pageConfigRepo.getByPageKey(pageKey);
    cache.set(cacheKey, config);
    this.setState((prev) => ({
      pageConfig: { ...prev.pageConfig, [pageKey]: config },
    }));
    return config;
  };

  refreshAll = async () => {
    cache.clear();
    await this.bootstrap();
  };

  getFromCache = (key) => cache.get(key) || null;

  render() {
    const value = {
      ...this.state,
      setLang: this.setLang,
      refreshAll: this.refreshAll,
      getFromCache: this.getFromCache,
    };

    return (
      <AppDataContext.Provider value={value}>
        {this.props.children}
      </AppDataContext.Provider>
    );
  }
}
